import { FC, useState } from 'react';
import { ArrowLeft, Target } from 'lucide-react';
import type { MoveUnitOption } from './MoveFocusPopover';

interface Props {
  roadmapName: string;
  units: MoveUnitOption[];
  /** The unit the focus is currently on — shown as "Active focus" and not selectable. */
  currentUnitId: string;
  /** The draft destination (highlighted row). */
  selectedUnitId: string;
  onSelect: (unitId: string) => void;
  onBack: () => void;
  onDone: () => void;
}

const LINK =
  'tw-inline-flex tw-items-center tw-gap-1 tw-cursor-pointer tw-border-none tw-bg-transparent tw-px-0 tw-py-1 tw-font-atkinson tw-text-sm tw-font-bold tw-text-indigo-600';

// The "Unit NN" pill on the left of each row. Dark for the current (active) focus, light otherwise.
const badgeClass = (dark: boolean) =>
  `tw-shrink-0 tw-whitespace-nowrap tw-rounded-md tw-px-2 tw-py-0.5 tw-font-atkinson tw-text-xs tw-font-bold ${
    dark ? 'tw-bg-stone-800 tw-text-neutral-0' : 'tw-border tw-border-stone-200 tw-bg-stone-50 tw-text-stone-800'
  }`;

export const UnitSelectStep: FC<Props> = ({
  roadmapName,
  units,
  currentUnitId,
  selectedUnitId,
  onSelect,
  onBack,
  onDone,
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-flex tw-items-center tw-gap-3 tw-border-b-2 tw-border-neutral-100 tw-px-4 tw-pb-2 tw-pt-4">
        <button type="button" onClick={onBack} className={LINK}>
          <ArrowLeft className="tw-size-4" />
          Back
        </button>
        <span className="tw-font-sora tw-text-sm tw-font-bold tw-text-neutral-950">Select a unit to move to</span>
      </div>

      <div className="tw-flex tw-max-h-[50vh] tw-flex-col tw-gap-2 tw-overflow-y-auto tw-p-4">
        <span className="tw-font-atkinson tw-text-xs tw-text-neutral-700">
          {roadmapName} · {units.length} {units.length === 1 ? 'Unit' : 'Units'}
        </span>

        {units.map(u => {
          const isCurrent = u.value === currentUnitId;
          const isSelected = u.value === selectedUnitId && !isCurrent;
          const isHovered = hoveredId === u.value;
          // The current focus reads as a filled gray row; hovering a selectable unit previews the same gray.
          const bg = isSelected ? 'tw-bg-indigo-50' : isCurrent || isHovered ? 'tw-bg-stone-50' : 'tw-bg-neutral-0';
          const border = isSelected
            ? 'tw-border-[#c2c9fe]'
            : isCurrent
            ? 'tw-border-neutral-200'
            : 'tw-border-transparent';
          return (
            <button
              key={u.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`${u.unitName}: ${u.summary}${isCurrent ? ', active focus' : ''}`}
              disabled={isCurrent}
              onClick={isCurrent ? undefined : () => onSelect(u.value)}
              onMouseEnter={() => setHoveredId(u.value)}
              onMouseLeave={() => setHoveredId(prev => (prev === u.value ? null : prev))}
              className={`tw-flex tw-w-full tw-items-center tw-gap-3 tw-rounded-xl tw-border-2 tw-px-3 tw-py-[10px] tw-text-left ${bg} ${border} ${
                isCurrent ? 'tw-cursor-default' : 'tw-cursor-pointer'
              }`}
            >
              <span className={badgeClass(isCurrent)}>{u.unitName}</span>
              <span
                className={`tw-min-w-0 tw-flex-1 tw-truncate tw-font-atkinson tw-text-base tw-leading-5 tw-text-neutral-950 ${
                  isCurrent ? 'tw-font-bold' : 'tw-font-normal'
                }`}
              >
                {u.summary}
              </span>
              {isCurrent && (
                <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-indigo-600 tw-px-2 tw-py-1 tw-font-atkinson tw-text-xs tw-font-bold tw-text-neutral-0">
                  <Target size={14} aria-hidden />
                  Active focus
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="tw-border-t-2 tw-border-neutral-100 tw-px-4 tw-py-3">
        <button
          type="button"
          onClick={onDone}
          className="tw-w-full tw-cursor-pointer tw-rounded-full tw-border-none tw-bg-indigo-600 tw-px-6 tw-py-3 tw-font-atkinson tw-text-base tw-font-bold tw-leading-5 tw-text-neutral-0"
        >
          Done
        </button>
      </div>
    </div>
  );
};
